import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, mergeMap, retry, shareReplay } from 'rxjs/operators';

import { STATIC_MENU_URL_MAP } from './constants';
import {
    BOTH_DEFAULT_AND_LEGACY_DATA,
    DEFAULT_ONLY_DATA,
    LEGACY_ONLY_DATA,
} from 'AccountsApp/ts/constants/user-preferences.constants';
import { LEGACY_DATA_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { DataMenuOptionType } from 'AccountsApp/ts/types/user-preference.type';
import { StaticMenuObservable } from 'Angular/services/static-menus/types/static-menu-observable.type';
import {
    StaticInfoMenuType,
    StaticMenuType,
    StaticMetricMenuType,
} from 'Angular/services/static-menus/types/static-menu-type.type';
import { StaticInfoFieldLabelMap } from 'Angular/services/static-menus/interfaces/static-info-field-label-map.interface';
import {
    StaticInfoFieldMenuCategory,
    StaticInfoFieldMenuItem,
} from 'Angular/services/static-menus/interfaces/static-info-field-menu.interface';
import { StaticMetricLabelMap } from 'Angular/services/static-menus/interfaces/static-metric-label-map.interface';
import {
    StaticMetricMenuCategory,
    StaticMetricMenuItem,
    StaticMetricMenuSection,
} from 'Angular/services/static-menus/interfaces/static-metric-menu.interface';
import { CustomPDFReportBuilderStore } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/state/interfaces/custom-pdf-report-builder-store.interface';

import { AjaxService } from '../core/ajax.service';
import { StaticFileUrlService } from '../static-file-url.service';
import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

/**
 * Static Menu Service
 *
 * A service that fetches menu data stored in .json files (because they are not expected to change).
 */
@Injectable({
    providedIn: 'root',
})
export class StaticMenuService {
    // Filter menu entries

    private userHasLegacyDataFeature: boolean;

    private userDataMenuSetting: DataMenuOptionType = BOTH_DEFAULT_AND_LEGACY_DATA;

    // Cache

    private cachedMenuMap: {
        [k: string]: {
            [defaultOnly: string]: StaticMenuObservable;
        };
    } = {};

    constructor(
        private ajaxService: AjaxService,
        private staticFileUrlService: StaticFileUrlService,
        private featureService: FeatureService,
        private accountsService: AccountsService,
        private readonly store: Store<{ reducers: CustomPDFReportBuilderStore }>
    ) {
        this.userHasLegacyDataFeature = this.featureService.userHasFeatures([LEGACY_DATA_FEATURE]);
        this.accountsService.getUserPreferences().subscribe((preference) => {
            this.userDataMenuSetting = preference.data_menu_setting;
        });
    }

    /* eslint-disable no-dupe-class-members */
    // Using overload signatures so that TypeScript can infer what the observable return type will be based on the menuType parameter
    getStaticMenu(
        menuType: 'metricMenu',
        submenuType: StaticMetricMenuType | StaticInfoMenuType,
        defaultOnly?: boolean
    ): Observable<StaticMetricMenuCategory[]>;

    getStaticMenu(
        menuType: 'metricLabelMap',
        submenuType: StaticMetricMenuType | StaticInfoMenuType,
        defaultOnly?: boolean
    ): Observable<StaticMetricLabelMap>;

    getStaticMenu(
        menuType: 'infoFieldMenu',
        submenuType: StaticMetricMenuType | StaticInfoMenuType,
        defaultOnly?: boolean
    ): Observable<StaticInfoFieldMenuCategory[]>;

    getStaticMenu(
        menuType: 'infoFieldLabelMap',
        submenuType: StaticMetricMenuType | StaticInfoMenuType,
        defaultOnly?: boolean
    ): Observable<StaticInfoFieldLabelMap>;

    /**
     * Given the menu type and group of menu items, get the absolute URL to that file and retrieve the json data.
     * The json data describes the categoriesg in each menu, and any sublist items under those categories.
     *
     * @param menuType The type of menu data that is fetched for i.e. metric menu.
     * @param submenuType A specific group of items under menuType i.e. popular metrics.
     * @param defaultOnly optional param designed to be used if the request is coming from a page that has its own data source override and we are on the default view
     */
    getStaticMenu(
        menuType: StaticMenuType,
        submenuType: StaticMetricMenuType | StaticInfoMenuType,
        defaultOnly?: boolean
    ): StaticMenuObservable {
        // eslint-disable-next-line no-param-reassign
        defaultOnly = defaultOnly || false;
        const staticJsonUrl = STATIC_MENU_URL_MAP[menuType][submenuType];
        const cachedMenu$ = this.cachedMenuMap[staticJsonUrl]?.[String(defaultOnly)];

        if (cachedMenu$) {
            return cachedMenu$;
        }

        const menu$ = this.staticFileUrlService
            .getAbsoluteURL(staticJsonUrl)
            .pipe(
                mergeMap((cachedUrl) => {
                    return this.ajaxService.getDataObserveBody(cachedUrl, undefined, {
                        'Cache-Control': 'max-age=31536000', // Cache for 1 year
                    });
                }),
                retry(3),
                shareReplay(1)
            )
            .pipe(
                map((value) => {
                    switch (menuType) {
                        case 'metricMenu':
                            return this.filterCalcMenuCategories(value as StaticMetricMenuCategory[], defaultOnly);
                        case 'infoFieldMenu':
                            return this.filterInfoFieldMenuCategories(
                                value as StaticInfoFieldMenuCategory[],
                                defaultOnly
                            );
                        default: // if we are retrieving label maps, there is nothing to filter
                            return value;
                    }
                })
            );
        if (!this.cachedMenuMap[staticJsonUrl]) {
            this.cachedMenuMap[staticJsonUrl] = {};
        }
        this.cachedMenuMap[staticJsonUrl][String(defaultOnly)] = menu$ as StaticMenuObservable;
        return menu$ as StaticMenuObservable;
    }

    // Filter info field menu categories

    private filterInfoFieldMenuCategories(
        categories: StaticInfoFieldMenuCategory[],
        defaultOnly: boolean
    ): StaticInfoFieldMenuCategory[] {
        const filteredCategories = categories.map(
            (c): StaticInfoFieldMenuCategory => ({ ...c, list_items: this.filterMenuItems(c.list_items, defaultOnly) })
        );
        return filteredCategories.filter((c) => c.list_items.length > 0);
    }

    // Filter calc field menu categories

    private filterCalcMenuCategories(
        categories: StaticMetricMenuCategory[],
        defaultOnly: boolean
    ): StaticMetricMenuCategory[] {
        const filteredCategories: StaticMetricMenuCategory[] = categories.map(
            (c): StaticMetricMenuCategory => ({
                ...c,
                sections: this.filterCalcMenuSections(c.sections, c.category_label, defaultOnly),
            })
        );

        return filteredCategories.filter((c) => c.sections.length > 0);
    }

    /**
     * Filter the calc menu sections.
     *
     * This filters in two ways:
     * 1. First, it filters out complete sections based on legacy data feature and data menu settings:
     *   a. If the data menu settings is BOTH_DEFAULT_AND_LEGACY_DATA, it does not filter the sections here.
     *   c. If the user does not have the legacy data feature, it filters out all sections with `(Morn)` suffix.
     *   b. If the user has the legacy data feature and data menu settings is DEFAULT_ONLY_DATA it filters out all
     *      sections with `(Morn)` suffix.
     *   c. If the user has the legacy data feature and the data menu settings is LEGACY_ONLY_DATA, it filters all
     *      default sections with a `(Morn)` counterpart.
     * 2. Filters sections that end up with no items after filtering the section items.
     *
     * @private
     */
    private filterCalcMenuSections(
        sections: StaticMetricMenuSection[],
        categoryLabel: string,
        defaultOnly: boolean
    ): StaticMetricMenuSection[] {
        let filteredSections: StaticMetricMenuSection[];
        if (this.userHasLegacyDataFeature && this.userDataMenuSetting === BOTH_DEFAULT_AND_LEGACY_DATA) {
            // If the user has the legacy data feature and its data menu setting is 'both', then we don't need to filter
            // out any sections
            filteredSections = sections;
            // some menu sections should only be visible to specific client groups
        } else if (categoryLabel === 'Firm-Specific Asset Classifications') {
            filteredSections = sections.filter((s) => {
                return this.featureService.userInClientGroup(s.section_label);
            });
        } else {
            // We filter out M* sections if the user does not have the legacy data feature or if its data menu setting
            // is default only
            const filterMorningstarSections =
                !this.userHasLegacyDataFeature || this.userDataMenuSetting === DEFAULT_ONLY_DATA;
            const section_labels = sections.map((s) => s.section_label);
            filteredSections = sections.filter((s) => {
                if (filterMorningstarSections) return !s.section_label.endsWith(' (Morn)');
                return !section_labels.includes(`${s.section_label} (Morn)`);
            });
        }
        const sectionsWithFilteredItems = filteredSections.map(
            (s): StaticMetricMenuSection => ({ ...s, items: this.filterMenuItems(s.items, defaultOnly) })
        );
        const nonEmptySections = sectionsWithFilteredItems.filter((s) => s.items.length > 0);
        if (this.userHasLegacyDataFeature && this.userDataMenuSetting === LEGACY_ONLY_DATA)
            return nonEmptySections.map((s) => {
                if (!s.section_label.endsWith(' (Morn)')) return s;
                return { ...s, section_label: s.section_label.slice(0, -7) };
            });
        return nonEmptySections;
    }

    // Shared filter logic for calc and info field menu items
    // Using overload signatures so that TypeScript can infer what the observable return type will be based on the items parameter

    private filterMenuItems(items: StaticMetricMenuItem[], defaultOnly: boolean): StaticMetricMenuItem[];

    private filterMenuItems(items: StaticInfoFieldMenuItem[], defaultOnly: boolean): StaticInfoFieldMenuItem[];

    private filterMenuItems(
        items: StaticMetricMenuItem[] | StaticInfoFieldMenuItem[],
        defaultOnly: boolean
    ): StaticMetricMenuItem[] | StaticInfoFieldMenuItem[] {
        return items.filter((i) => {
            // Display element if
            //     always_display is true
            //     of, if the user has legacy data:
            //         if the user data menu setting is legacy only, ignore if is default
            //         if the user data menu setting is default only, ignore if is legacy
            //     or no feature is needed
            //     or user has the required feature_needed
            if (i.always_display) return true;
            if (this.userHasLegacyDataFeature) {
                // eslint-disable-next-line eqeqeq
                if (!defaultOnly) {
                    // if there is no context defaultOnly override we go based off data menu setting preference
                    if (this.userDataMenuSetting === LEGACY_ONLY_DATA && i.is_default_version) {
                        return false;
                    }
                    if (
                        this.userDataMenuSetting === DEFAULT_ONLY_DATA &&
                        i.features_needed?.includes(LEGACY_DATA_FEATURE)
                    ) {
                        return false;
                    }
                } // if there is a context defaultOnly override we filter out items that need legacy data feature
                else if (i.features_needed?.includes(LEGACY_DATA_FEATURE)) {
                    return false;
                }
            }
            return this.featureService.userCanAccess(i);
        });
    }
}
